import { useEffect, useId, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface UseDialogA11yOptions {
  /** Close the dialog on Escape (default: true). */
  closeOnEscape?: boolean;
  /** Move focus into the dialog on mount (default: true). */
  autoFocus?: boolean;
}

/**
 * Accessibility helper for hand-rolled modal/dialog overlays.
 *
 * Returns a `dialogRef` (attach to the dialog panel that should carry
 * `role="dialog"` / `aria-modal="true"`) and a `titleId` (attach to the
 * heading and reference via `aria-labelledby`).
 *
 * Handles: Escape-to-close, moving focus into the dialog on open,
 * restoring focus to the previously focused element on close, and
 * trapping Tab focus within the dialog. Purely behavioural — it does not
 * change any visual styling.
 */
export function useDialogA11y(onClose?: () => void, options?: UseDialogA11yOptions) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const closeOnEscape = options?.closeOnEscape !== false;
  const autoFocus = options?.autoFocus !== false;

  useEffect(() => {
    const node = dialogRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    if (autoFocus && node) {
      // Only steal focus if it isn't already inside the dialog.
      if (!node.contains(document.activeElement)) {
        const focusables = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusables.length > 0) {
          focusables[0].focus();
        } else {
          node.focus();
        }
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose?.();
        return;
      }

      if (e.key === 'Tab' && node) {
        const focusables = Array.from(
          node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => el.offsetParent !== null || el === document.activeElement);

        if (focusables.length === 0) {
          e.preventDefault();
          node.focus();
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;

        if (e.shiftKey) {
          if (active === first || !node.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else if (active === last || !node.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
     
  }, [onClose, closeOnEscape, autoFocus]);

  return { dialogRef, titleId };
}
